/**
 * Sprint V38 (So 15.09.2026) — Share-Button V2 mit Referral-Kopplung,
 * Text-Varianten, Dark-Mode, Copy-Link-Fallback und Multi-Context-Tracking.
 *
 * Kontexte:
 *   - `quickcheck_result`   Direkt nach QuickCheck-Ergebnis
 *   - `mein_muster`         Auf /mein-muster (Steckbrief)
 *   - `kern_zitat`          Einzelne Kern-Zitat-Karte (mit Zitat-Payload)
 *
 * Verhalten:
 *   - Erzeugt Instagram-Story-PNG via /api/share/quickcheck-image
 *   - Fügt ?ref=<referralCode> in Landing-URL an, wenn User Code hat
 *   - navigator.share() (Mobile) → PNG-Download (Desktop) → Copy-Link-Fallback
 *   - 4 Text-Varianten (personal / provokativ / linkedin / neugier) mit Dropdown
 *   - Tracking via /api/share/track (context, variant, channel)
 */
import React, { useMemo, useState } from "react";
import { API } from "@/lib/api";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";
import {
  Share2, Download, Loader2, CheckCircle2, AlertCircle,
  Copy, ChevronDown, MessageSquare,
} from "lucide-react";

const SLUG_EN_TO_DE = { bee: "biene", owl: "eule", cat: "katze", dog: "hund" };
const LANDING_URL = "https://flex4success.effektiv-kommunizieren.de";

function buildVariants(fokustypName, quote = null) {
  return {
    personal: {
      label: "Persönlich",
      text: `Mein Fokustyp: ${fokustypName}. Erklärt mehr über mich als 10 Persönlichkeitstests. Was ist deiner?`,
    },
    provokativ: {
      label: "Provokativ",
      text: `Ich bin ${fokustypName}. Und du? Test-Link inside — 5 Fragen, 2 Minuten, ein Aha-Moment.`,
    },
    linkedin: {
      label: "LinkedIn",
      text: quote
        ? `„${quote}" — trifft für mich als ${fokustypName} genau ins Schwarze. Wie tickst du?`
        : `Warum manche Konflikte im Team wiederkehren — und wie ich sie jetzt erkenne. Mein Fokustyp: ${fokustypName}.`,
    },
    neugier: {
      label: "Neugier",
      text: `5 Fragen. 2 Minuten. Danach weißt du, wie du wirklich tickst. Mein Muster: ${fokustypName}.`,
    },
  };
}

function downloadBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ShareFokustypButtonV2({
  fokustyp,
  referralCode = null,
  context = "mein_muster",
  quote = null,
  testId = "share-fokustyp-button-v2",
  compact = false,
}) {
  const fokustypDe = SLUG_EN_TO_DE[fokustyp] || fokustyp;
  const profile = FOKUSTYP_PROFILES?.[fokustyp in FOKUSTYP_PROFILES ? fokustyp : Object.keys(SLUG_EN_TO_DE).find((k) => SLUG_EN_TO_DE[k] === fokustypDe)] || null;
  const fokustypName = profile?.name || fokustypDe || "Fokustyp";

  const variants = useMemo(() => buildVariants(fokustypName, quote), [fokustypName, quote]);
  const [variantKey, setVariantKey] = useState("personal");
  const [variantOpen, setVariantOpen] = useState(false);
  const [state, setState] = useState("idle");
  const [copyState, setCopyState] = useState("idle");

  // Sprint V41 (Mo 15.09.2026, CoS-Quick-Win) — Datenkrake-Aufraeumen.
  // Vorher: 4 Query-Parameter (Referral + drei Marketing-Tracker) → sieht in
  //         WhatsApp/E-Mail wie eine Datenkrake aus.
  // Nachher: `/via/CODE` (mit Referral) oder nackte URL (ohne).
  // Tracking (context/channel/variant/fokustyp) laeuft komplett server-side
  // via POST /api/share/track — kein Marketing-Rattenschwanz mehr im Link.
  const shareUrl = referralCode
    ? `${LANDING_URL}/via/${encodeURIComponent(referralCode)}`
    : `${LANDING_URL}/`;

  const finalText = `${variants[variantKey].text} ${shareUrl}`;

  async function track(channel) {
    try {
      await fetch(`${API}/share/track`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fokustyp: fokustypDe,
          channel,
          context,
          variant: variantKey,
          has_referral: Boolean(referralCode),
        }),
      });
    } catch { /* silent */ }
  }

  async function handleShare() {
    if (state === "loading" || !fokustypDe) return;
    setState("loading");
    try {
      const url = `${API}/share/quickcheck-image?fokustyp=${encodeURIComponent(fokustypDe)}`;
      const resp = await fetch(url, { method: "POST", credentials: "include" });
      if (!resp.ok) throw new Error(`render_${resp.status}`);
      const blob = await resp.blob();
      const file = new File([blob], `mein-fokustyp-${fokustypDe}.png`, { type: "image/png" });

      let channel = "download";
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `Mein Fokustyp: ${fokustypName}`,
            text: variants[variantKey].text,
            url: shareUrl,
          });
          channel = "native";
        } catch (err) {
          if (err && err.name === "AbortError") { setState("idle"); return; }
          downloadBlob(blob, `mein-fokustyp-${fokustypDe}.png`);
        }
      } else {
        downloadBlob(blob, `mein-fokustyp-${fokustypDe}.png`);
      }
      track(channel);
      setState("done");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  }

  async function handleCopy() {
    const ok = await copyToClipboard(finalText);
    setCopyState(ok ? "done" : "error");
    if (ok) track("copy_link");
    setTimeout(() => setCopyState("idle"), 2000);
  }

  return (
    <div className="inline-flex flex-col gap-2" data-testid={`${testId}-wrapper`} data-context={context}>
      {/* Variant-Selector */}
      {!compact && (
        <div className="relative inline-flex items-center gap-2" data-testid={`${testId}-variant-row`}>
          <MessageSquare className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" aria-hidden="true" />
          <button
            type="button"
            onClick={() => setVariantOpen(!variantOpen)}
            data-testid={`${testId}-variant-toggle`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-[var(--fts-primary-bright)]"
            aria-expanded={variantOpen}
          >
            Text-Variante: <span className="underline underline-offset-2">{variants[variantKey].label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${variantOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {variantOpen && (
            <div
              className="absolute top-full mt-1 z-10 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg p-1 min-w-[180px]"
              data-testid={`${testId}-variant-menu`}
            >
              {Object.entries(variants).map(([key, v]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setVariantKey(key); setVariantOpen(false); }}
                  data-testid={`${testId}-variant-${key}`}
                  className={`block w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 ${variantKey === key ? "font-bold text-[var(--fts-primary-bright)]" : "text-slate-700 dark:text-slate-200"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="inline-flex flex-wrap items-center gap-2">
        {/* Primary Share Button */}
        <button
          type="button"
          onClick={handleShare}
          disabled={state === "loading" || !fokustypDe}
          data-testid={testId}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 transition-opacity"
        >
          {state === "loading" ? (
            <><Loader2 className="w-4 h-4 animate-spin" /><span>Bild wird vorbereitet …</span></>
          ) : state === "done" ? (
            <><CheckCircle2 className="w-4 h-4" /><span>Fertig</span></>
          ) : state === "error" ? (
            <><AlertCircle className="w-4 h-4" /><span>Nochmal versuchen</span></>
          ) : (
            <>
              {typeof navigator !== "undefined" && navigator.share
                ? <Share2 className="w-4 h-4" />
                : <Download className="w-4 h-4" />}
              <span>Meinen Fokustyp teilen</span>
            </>
          )}
        </button>

        {/* Copy-Link Fallback */}
        <button
          type="button"
          onClick={handleCopy}
          data-testid={`${testId}-copy-link`}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-2.5 transition-colors"
          title={referralCode ? "Text + Link mit deinem Referral-Code kopieren" : "Text + Link kopieren"}
        >
          {copyState === "done"
            ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /><span>Kopiert</span></>
            : copyState === "error"
              ? <><AlertCircle className="w-3.5 h-3.5 text-red-500" /><span>Fehler</span></>
              : <><Copy className="w-3.5 h-3.5" /><span>Link kopieren</span></>}
        </button>
      </div>

      {referralCode && !compact && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid={`${testId}-ref-hint`}>
          Enthält deinen Referral-Code <span className="font-mono font-semibold">{referralCode}</span> — wer sich registriert, bringt euch beiden den Bonus.
        </p>
      )}
    </div>
  );
}
